<!--
  @component Navbar.Search

  A search component for the navbar that provides an input field and handles search functionality.

  @prop value - The current value of the search input (bindable)
  @prop onsubmit - Function to be called when the search is submitted
  @prop label - The accessible label for the search input
  @prop placeholder - Placeholder text for the search input
  @prop css - Custom CSS styles to apply to the search component

  Example:
  <Navbar.Search
    bind:value={searchValue}
    onsubmit={handleSearch}
    label="Search"
    placeholder="Enter search term"
  >
    <Navbar.SearchOverlay>
      ... Search results or suggestions
    </Navbar.SearchOverlay>
  </Navbar.Search>
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { searchStyles } from '../navbar.styles';
  import { getContext, tick } from 'svelte';
  import TextField from '../../text-field/text-field.svelte';
  import { MagnifyingGlass } from 'phosphor-svelte';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarSearchProps } from '../navbar.types';

  const styles = searchStyles.raw();

  /** Props for the Navbar.Search component */
  let { value = $bindable(''), maxLength, onsubmit, children, label, placeholder, inputStyles, formStyles, labelStyles }: NavbarSearchProps = $props();

  /** Navbar store for managing search state */
  const navbarStore = getContext<NavbarStore>('navbar');

  /**
   * Handles the form submission
   * @param event - The submit event
   */
  const handleSubmit = (event: Event) => {
    event.preventDefault();
    onsubmit(value);
  }

  /**
   * Opens the search overlay when the input is clicked
   */
  const onclick = () => {
    navbarStore.openSearch();
  }

  /**
   * Handles keydown events for the search input
   * @param event - The keydown event
   */
  const onkeydown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        navbarStore.closeSearch();
        break;
      case 'Enter':
        if (navbarStore.isSearchOpen) {
          handleSubmit(event);
        } else {
          navbarStore.openSearch();
        }
        break;
    }
  }

  /**
   * Handles blur events for the search input
   * @param e - The blur event
   */
  const onblur = async (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    await tick();

    // console.log('navbarStore.keepSearchOpen', relatedTarget?.getAttribute('data-type'), navbarStore.keepSearchOpen);

    // Notice: adding value in the condition below will block the search overlay from closing when a click is made in a valid part on the navbar
    if ((/* value || */ navbarStore.keepSearchOpen || relatedTarget?.getAttribute('data-type') === 'link-wrapper') || (
      relatedTarget?.getAttribute('data-type') === 'menu-toggle' ||
      relatedTarget?.parentElement?.getAttribute('data-type') === 'drawer-toggle' ||
      relatedTarget?.parentElement?.getAttribute('data-type') === 'search-overlay' ||
      relatedTarget?.getAttribute('data-type') === 'search-overlay'
    )) {
      // console.log('navbar-search.onblur: not closing search');
      return;
    }

    // console.log('navbar-search.onblur: closing search');
    navbarStore.closeSearch();
  }

  $effect(() => {
    if (navbarStore.isMenuOpen) {
      navbarStore.triggerKeepSearchOpen()
    }
  })

  /**
   * Effect to open search when value changes
   */
  $effect(() => {
    if (value) {
      navbarStore.openSearch();
    }
  })
</script>

<form onsubmit={handleSubmit} class={css(styles.form, formStyles)}>
  <TextField
    {onblur}
    {onkeydown}
    {onclick}
    hideLabel
    floatingLabel
    type="search"
    bind:value
    name="search"
    label={label}
    placeholder={placeholder}
    maxLength={maxLength}
    rootStyles={styles.inputRoot}
    inputStyles={css.raw(styles.input, inputStyles)}
    labelStyles={labelStyles}
  >
    {#snippet trailingIcon()}
      <MagnifyingGlass />
    {/snippet}
  </TextField>
  {#if children}
    {@render children()}
  {/if}
</form>
